import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { MapPin, Phone, Mail, Globe, ChevronDown } from "lucide-react";
import logo from "../assets/logo2.png";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { Button } from "./ui/button";
import { useLanguage } from "../contexts/LanguageProvider";
import { CONTACT_INFO, GOOGLE_MAPS_URL } from "../config/constants";
import { useImages, usePreloadImages, AccommodationLightbox } from "@agape/frontend-utils";
import { fetchCertificateImages } from "../lib/galleryUtils";
import { supabase } from '../config/supabase';

function LayoutContent({ children }) {
  const location = useLocation();
  const { language, setLanguage, t } = useLanguage();
  useImages(); // Load images for context
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [selectedRoomImages, setSelectedRoomImages] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [certificateImages, setCertificateImages] = useState([]);

  // Preload certificate images on component mount
  useEffect(() => {
    async function preloadCertificateImages() {
      try {
        const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
        if (!supabaseUrl) {
          throw new Error('Missing REACT_APP_SUPABASE_URL environment variable');
        }
        const images = await fetchCertificateImages(supabase, supabaseUrl);
        setCertificateImages(images);
      } catch (error) {
        console.error('Error preloading certificate images:', error);
      }
    }
    
    preloadCertificateImages();
  }, []);

  // Preload certificate image URLs for instant display
  usePreloadImages(certificateImages, { urlProperty: 'url' });

  const navigationItems = [
    { key: "home", url: "/" },
    { key: "accommodations", url: "/accommodations" },
    { key: "usefulInformation", url: "/useful-information" },
    { key: "localArea", url: "/local-area" },
    { key: "about", url: "/about" },
    { key: "contact", url: "/contact" },
  ];

  const bannerImages = {
    en: "https://qtrypzzcjebvfcihiynt.supabase.co/storage/v1/object/public/base44-prod/public/68b73d87266c51e8e2de157b/fe0d33fea_agape_pinned_image.jpg",
    gr: "https://qtrypzzcjebvfcihiynt.supabase.co/storage/v1/object/public/base44-prod/public/68b73d87266c51e8e2de157b/fb984d38d_agape_pinned_image_gr.jpg"
  };

  const openCertificateGallery = () => {
    // Use preloaded certificate images for instant display
    if (certificateImages.length > 0) {
      setSelectedRoomImages(certificateImages);
      setCurrentIndex(0);
      setLightboxOpen(true);
    } else {
      // Fallback to original PDF if no certificate images found
      console.log('No certificate images found, opening PDF fallback');
      window.open('https://www.pyrgicretanlivingandspa.gr/_gfx/espa/afisa.pdf', '_blank');
    }
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
    setSelectedRoomImages([]);
    setCurrentIndex(0);
  };

  const navigateImage = (direction) => {
    const newIndex = direction === 'next'
      ? (currentIndex + 1) % selectedRoomImages.length
      : (currentIndex - 1 + selectedRoomImages.length) % selectedRoomImages.length;
    setCurrentIndex(newIndex);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <style>
        {`
          :root {
            --primary-blue: #0f4c75;
            --secondary-blue: #3282b8;
            --accent-gold: #f4a261;
            --warm-terracotta: #e76f51;
            --soft-cream: #faf7f0;
            --deep-navy: #2d3436;
          }
        `}
      </style>

      {/* Top Banner */}
      <div className="hidden md:block bg-gradient-to-r from-blue-900 to-blue-800 text-white py-2 px-4">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row justify-between items-center text-sm">
          <div className="flex items-center gap-4 mb-2 sm:mb-0">
            <div className="flex items-center gap-1">
              <Phone className="w-3 h-3" aria-hidden="true" />
              <span>{CONTACT_INFO.PHONE}</span>
            </div>
            <div className="flex items-center gap-1">
              <Mail className="w-3 h-3" aria-hidden="true" />
              <span>{CONTACT_INFO.EMAIL}</span>
            </div>
          </div>
          <a href={GOOGLE_MAPS_URL} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-blue-200 hover:underline">
            <MapPin className="w-3 h-3" aria-hidden="true" />
            <span>{CONTACT_INFO.ADDRESS}</span>
          </a>
        </div>
      </div>

      {/* Main Header */}
      <header className="bg-white shadow-lg sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-28">
            <div className="flex items-center gap-2">
              {/* Logo */}
              <Link to="/" className="flex items-center gap-3">
                <img src={logo} alt="Agape Guesthouse Logo" className="w-32 h-32 object-contain rounded-full" />
                <div>
                  <h1 className="text-2xl font-bold text-gray-900">Agape Community<br /><span style={{textAlign: 'center', display: 'block'}}>Guesthouse</span></h1>
                </div>
              </Link>
            </div>

            <div className="flex items-center gap-6">
              {/* Desktop Navigation */}
              <nav className="hidden lg:flex items-center space-x-8">
                {navigationItems.map((item) => (
                  <Link
                    key={item.key}
                    to={item.url}
                    className={`text-sm font-medium transition-all duration-300 hover:text-blue-600 relative ${
                      location.pathname === item.url
                        ? "text-blue-600"
                        : "text-gray-700"
                    }`}
                  >
                    {t(item.key)}
                    {location.pathname === item.url && (
                      <div className="absolute -bottom-1 left-0 right-0 h-0.5 bg-blue-600 rounded-full" />
                    )}
                  </Link>
                ))}
              </nav>

              {/* Language Selector */}
              <div className="hidden lg:block">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="sm" className="flex items-center gap-2">
                      <Globe className="w-4 h-4" aria-hidden="true" />
                      <span className="text-sm font-medium">{language.toUpperCase()}</span>
                      <ChevronDown className="w-3 h-3" aria-hidden="true" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start">
                    <DropdownMenuItem onClick={() => setLanguage('en')} className="cursor-pointer">
                      <span className="text-sm">🇬🇧 English</span>
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => setLanguage('gr')} className="cursor-pointer">
                      <span className="text-sm">🇬🇷 Ελληνικά</span>
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>

              {/* Mobile Menu Button */}
              <div className="lg:hidden relative">
                <input type="checkbox" id="mobile-menu-toggle" className="hidden peer" />
                <label
                  htmlFor="mobile-menu-toggle"
                  className="cursor-pointer flex flex-col gap-1 p-2 relative z-50"
                  aria-label={t('mobileMenu')}
                  title={t('mobileMenu')}
                >
                  <span className="sr-only">{t('mobileMenu')}</span>
                  <div className="w-6 h-0.5 bg-gray-700 transition-all peer-checked:rotate-45 peer-checked:translate-y-1.5"></div>
                  <div className="w-6 h-0.5 bg-gray-700 transition-all peer-checked:opacity-0"></div>
                  <div className="w-6 h-0.5 bg-gray-700 transition-all peer-checked:-rotate-45 peer-checked:-translate-y-1.5"></div>
                </label>

                {/* Mobile Menu */}
                <div className="fixed top-28 left-0 right-0 bg-white shadow-lg transform -translate-y-full opacity-0 transition-all duration-300 peer-checked:translate-y-0 peer-checked:opacity-100 z-40 border-t border-gray-200 pointer-events-none peer-checked:pointer-events-auto">
                  
                  <nav className="flex flex-col p-4 space-y-4">
                    <div className="p-4 border-b border-gray-100">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="outline" className="w-full flex items-center gap-2">
                            <Globe className="w-4 h-4" aria-hidden="true" />
                            <span className="text-sm font-medium">{language.toUpperCase()}</span>
                            <ChevronDown className="w-3 h-3" aria-hidden="true" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="start" className="w-full">
                          <DropdownMenuItem onClick={() => setLanguage('en')} className="cursor-pointer">
                            <span className="text-sm">🇬🇧 English</span>
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => setLanguage('gr')} className="cursor-pointer">
                            <span className="text-sm">🇬🇷 Ελληνικά</span>
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>

                    {navigationItems.map((item) => (
                      <Link
                        key={item.key}
                        to={item.url}
                        className={`text-base font-medium py-3 px-4 rounded-lg transition-colors block ${
                          location.pathname === item.url
                            ? "bg-blue-50 text-blue-600"
                            : "text-gray-700 hover:bg-gray-50"
                        }`}
                        onClick={() => {
                          // Close mobile menu when link is clicked
                          document.getElementById('mobile-menu-toggle').checked = false;
                        }}
                      >
                        {t(item.key)}
                      </Link>
                    ))}
                  </nav>
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1">
        {children}
      </main>

      {/* Footer */}
      <footer className="bg-gray-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Contact Info */}
            <div>
              <div className="flex items-center gap-3 mb-6">
                <img src={logo} alt="Agape Guesthouse Logo" className="w-20 h-20 object-contain rounded-full" />
                <div>
                  <h3 className="text-lg font-bold">Agape Community<br /><span style={{textAlign: 'center', display: 'block'}}>Guesthouse</span></h3>
                </div>
              </div>
              <div className="space-y-3 text-gray-300">
                <div className="flex items-center gap-2">
                  <MapPin className="w-4 h-4" aria-hidden="true" />
                  <span>{CONTACT_INFO.ADDRESS}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Phone className="w-4 h-4" aria-hidden="true" />
                  <span>{CONTACT_INFO.PHONE}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Mail className="w-4 h-4" aria-hidden="true" />
                  <span>{CONTACT_INFO.EMAIL}</span>
                </div>
              </div>
            </div>

            {/* Quick Links */}
            <div>
              <h4 className="text-lg font-semibold mb-6">{t('quickLinks')}</h4>
              <div className="grid grid-cols-2 gap-2">
                {navigationItems.map((item) => (
                  <Link
                    key={item.key}
                    to={item.url}
                    className="text-gray-400 hover:text-white transition-colors text-sm"
                  >
                    {t(item.key)}
                  </Link>
                ))}
              </div>
            </div>

            {/* Info */}
            <div>
              <h4 className="text-lg font-semibold mb-6">{t('experienceCrete')}</h4>
              <p className="text-gray-400 text-sm mb-4">
                {t('experienceDescription')}
              </p>
              <div className="flex items-center gap-2 text-sm">
                <span className="px-2 py-1 bg-blue-600 rounded text-xs">{t('freeWifi')}</span>
              </div>
            </div>
          </div>

          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-400 text-sm">
            <div className="mb-6 space-y-2 md:space-y-0 md:space-x-6">
              <a href="http://www.agrotikianaptixi.gr/el" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white transition-colors">
                {t('ruralDevelopment')}
              </a>
              <span className="hidden md:inline">|</span>
              <a href="https://ec.europa.eu/agriculture/index_el" target="_blank" rel="noopener noreferrer" className="block md:inline text-gray-400 hover:text-white transition-colors">
                {t('agricultureDirectorate')}
              </a>
            </div>
            <p>&copy; 2024 {CONTACT_INFO.GUESTHOUSE_NAME} Guesthouse. {t('footerAllRightsReserved')} | {t('footerCopyright')} | {t('footerWebsiteEstablished')}</p>
          </div>
        </div>
      </footer>

      {/* Pinned Banner */}
      <div className="fixed bottom-4 left-1/2 transform -translate-x-1/2 md:left-4 md:transform-none z-50">
        <button
          onClick={openCertificateGallery}
          className="block hover:scale-105 transition-transform duration-200 cursor-pointer"
          title="View EU Funding Certificate"
        >
          <img
            src={bannerImages[language]}
            alt="Co-funded by Greece and the European Union - Click to view certificate"
            className="w-auto h-16 md:h-20 bg-white p-2 rounded-md shadow-lg"
          />
        </button>
      </div>

      {/* Certificate Gallery */}
      <AccommodationLightbox
        isOpen={lightboxOpen}
        onClose={closeLightbox}
        photos={selectedRoomImages}
        currentIndex={currentIndex}
        onNavigate={navigateImage}
        roomName="EU Funding Certificate"
      />
    </div>
  );
}

export default function Layout({ children }) {
  return (
      <LayoutContent>{children}</LayoutContent>
  );
}
